import React, { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Spin, Tooltip } from 'antd';
import { CloseOutlined, WarningOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { presentationsApi } from '../../../api/presentationsApi';
import {
  IGalleryCandidate,
  IGalleryPicker,
  IGallerySlot,
} from '../../../types/models/presentations';
import {
  ManageGalleriesLink, PhotoPool, PickerBody, PickerFooter, boxWidth, keepOffered, slideOf,
} from './PhotoPool';

/**
 * Choose which photo fills each numbered image slot of a listing's brochure.
 *
 * Without picks, `image_N` is the Nth photo in database order, so the cover is
 * whichever image was uploaded first. Picking pins a slot; every other slot
 * keeps the default order minus the pinned photos. One set of picks serves every
 * template the listing uses — the ES and EN brochures share slot names, and a
 * teaser is cut from its brochure — which is why slots are shown per slide
 * rather than per template.
 *
 * Click a slot, then click a photo. Picking a photo that already sits in another
 * slot swaps the two, so a photo is never in two places at once.
 *
 * The amenities slide's captioned grid is its own screen, `AmenityPhotosModal`;
 * this one saves only `picks`, which leaves the grid's alone.
 *
 * Photos uploaded here go to the listing's Brochure gallery, so "Manage
 * galleries" opens the listing's galleries — it needs `listingSlug`, the key
 * the listings endpoint is read by.
 *
 * Trigger-as-children, same as `MapImageModal` and `FloorPlanWizard`.
 */

const Body = styled(PickerBody)`
  .slides {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-bottom: 24px;
  }

  .boxes {
    display: flex;
    gap: 6px;
  }

  .box.default img {
    opacity: 0.55;
  }

  .box .conflict {
    position: absolute;
    top: 4px;
    left: 4px;
    color: #faad14;
  }
`;

export const groupBySlide = (slots: IGallerySlot[]) => {
  const groups = new Map<number | null, IGallerySlot[]>();
  slots.forEach((slot) => {
    const slide = slideOf(slot);
    groups.set(slide, [...(groups.get(slide) ?? []), slot]);
  });
  return Array.from(groups.entries())
    .sort(([a], [b]) => (a ?? Infinity) - (b ?? Infinity))
    .map(([slide, group]) => ({ slide, slots: group }));
};

/**
 * What every slot shows for a given set of picks — the same rule as
 * `gallery.assign` in ancana_api: picks where made, default order elsewhere,
 * skipping photos already pinned.
 */
export const layout = (
  slots: IGallerySlot[],
  picks: Record<string, string>,
  defaultOrder: string[],
): Record<string, string | undefined> => {
  const used = new Set(Object.values(picks));
  const queue = defaultOrder.filter((ref) => !used.has(ref));
  const result: Record<string, string | undefined> = {};
  const numbered = (slot: string) => Number(slot.replace('image_', ''));
  const highest = Math.max(0, ...slots.map((s) => numbered(s.slot)));
  for (let n = 1; n <= highest; n += 1) {
    const slot = `image_${n}`;
    result[slot] = picks[slot] ?? queue.shift();
  }
  return result;
};

/** Put `ref` in `slot`. If it already sits in another pinned slot, swap. */
export const assignRef = (
  picks: Record<string, string>,
  slot: string,
  ref: string,
): Record<string, string> => {
  const next = { ...picks };
  const holder = Object.keys(picks).find((key) => key !== slot && picks[key] === ref);
  if (holder) {
    if (picks[slot]) next[holder] = picks[slot];
    else delete next[holder];
  }
  next[slot] = ref;
  return next;
};

const samePicks = (a: Record<string, string>, b: Record<string, string>) => {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return Array.from(keys).every((key) => a[key] === b[key]);
};

const conflictText = (slot: IGallerySlot) => (
  'Templates draw this slot at different shapes, so one photo cannot suit all '
  + 'of them: '
  + slot.boxes
    .map((box) => `${box.template_name} ${box.aspect ? box.aspect.toFixed(2) : '?'}`)
    .join(', ')
);

export interface GalleryPicksEditorProps {
  listingId: number;
  listingName?: string;
  /** For "Manage galleries"; without it the link is not offered. */
  listingSlug?: string;
  /**
   * Only these slots — one slide's, in the Prepare screen. Saving still sends
   * every pick, so the other slides' are kept.
   */
  slots?: string[];
  onSaved?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** In a modal: offered as Close, and called after a save. */
  onClose?: () => void;
}

export interface GalleryPicksModalProps
  extends Omit<GalleryPicksEditorProps, 'onClose' | 'onDirtyChange'> {
  children?: ReactNode;
}

/** The slots and the pool, with their own Save. Loads when mounted. */
export const GalleryPicksEditor = ({
  listingId, listingName, listingSlug, slots: only, onSaved, onDirtyChange, onClose,
}: GalleryPicksEditorProps) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [data, setData] = useState<IGalleryPicker>();
  const [picks, setPicks] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string>();
  const [uploading, setUploading] = useState(false);

  const onlyKey = only?.join('|');
  useEffect(() => {
    setLoading(true);
    setError(undefined);
    presentationsApi.galleryPicker(listingId)
      .then((result) => {
        setData(result);
        setPicks(result.picks);
        setSelected(result.slots.find((slot) => !only || only.includes(slot.slot))?.slot);
      })
      .catch((e: any) => setError(e?.message ?? 'Could not load the gallery.'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, onlyKey]);

  /** The pool again, after the gallery editor; unsaved picks are kept. */
  const reload = useCallback(() => {
    presentationsApi.galleryPicker(listingId)
      .then((result) => {
        setData(result);
        setPicks((current) => keepOffered(current, result.candidates, (ref) => ref));
      })
      .catch((e: any) => setError(e?.message ?? 'Could not load the gallery.'));
  }, [listingId]);

  const upload = async (files: File[]) => {
    setUploading(true);
    setError(undefined);
    try {
      const result = await presentationsApi.uploadGalleryPhotos(listingId, files);
      setData(result);
      const [first] = result.uploaded;
      if (selected && first) setPicks((current) => assignRef(current, selected, first));
    } catch (e: any) {
      setError(e?.response?.data?.files ?? e?.message ?? 'Could not upload.');
    } finally {
      setUploading(false);
    }
  };

  const byRef = useMemo(() => {
    const map: Record<string, IGalleryCandidate> = {};
    data?.candidates.forEach((candidate) => { map[candidate.ref] = candidate; });
    return map;
  }, [data]);

  const shown = useMemo(
    () => (data ? layout(data.slots, picks, data.default_order) : {}),
    [data, picks],
  );

  const slotOfRef = useMemo(() => {
    const map: Record<string, string> = {};
    Object.entries(picks).forEach(([slot, ref]) => { map[ref] = slot; });
    return map;
  }, [picks]);

  const dirty = !!data && !samePicks(picks, data.picks);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  const drawn = new Set(data?.slots.map((slot) => slot.slot));
  const shownSlots = (data?.slots ?? []).filter((slot) => !only || only.includes(slot.slot));

  const clear = (slot: string) => {
    const next = { ...picks };
    delete next[slot];
    setPicks(next);
  };

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      // Every slot that was or is pinned, with null for the cleared ones, so the
      // server's whole-set replacement cannot keep one we removed.
      const body: Record<string, string | null> = {};
      Object.keys({ ...data.picks, ...picks }).forEach((slot) => {
        body[slot] = picks[slot] ?? null;
      });
      await presentationsApi.saveGalleryPicks(listingId, body);
      // The saved set is the new baseline, so the screen is clean again.
      setData({ ...data, picks: { ...picks } });
      onSaved?.();
      onClose?.();
    } catch (e: any) {
      setError(e?.response?.data?.picks ?? e?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
        <Body>
          {error && <Alert type="error" message={String(error)} style={{ marginBottom: 16 }} />}
          {loading && <Spin />}

          {data && shownSlots.length === 0 && (
            <Alert
              type="info"
              showIcon
              message="None of this property's templates has a photo slot"
            />
          )}

          {data && shownSlots.length > 0 && (
            <>
              <div className="slides">
                {groupBySlide(shownSlots).map(({ slide, slots }) => (
                  <div key={slide ?? 'unknown'}>
                    <div className="slide-label">
                      {slide ? `Slide ${slide}` : 'Slide unknown'}
                    </div>
                    <div className="boxes">
                      {slots.map((slot) => {
                        const ref = shown[slot.slot];
                        const photo = ref ? byRef[ref] : undefined;
                        const pinned = !!picks[slot.slot];
                        return (
                          <button
                            type="button"
                            key={slot.slot}
                            aria-label={slot.slot}
                            className={[
                              'box',
                              selected === slot.slot ? 'selected' : '',
                              pinned ? '' : 'default',
                            ].join(' ')}
                            style={{ width: boxWidth(slot) }}
                            onClick={() => setSelected(slot.slot)}
                          >
                            {photo && <img src={photo.thumbnail} alt="" />}
                            <span className="slot-name">
                              {pinned ? slot.slot : `${slot.slot} · default`}
                            </span>
                            {pinned && (
                              <Tooltip title="Back to the default photo">
                                <span
                                  className="clear"
                                  aria-label={`clear ${slot.slot}`}
                                  onClick={(e) => { e.stopPropagation(); clear(slot.slot); }}
                                >
                                  <CloseOutlined />
                                </span>
                              </Tooltip>
                            )}
                            {slot.conflict && (
                              <Tooltip title={conflictText(slot)}>
                                <WarningOutlined className="conflict" />
                              </Tooltip>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <PhotoPool
                candidates={data.candidates}
                selected={selected}
                slotOf={(ref) => (drawn.has(slotOfRef[ref]) ? slotOfRef[ref] : undefined)}
                onChoose={(ref) => selected && setPicks(assignRef(picks, selected, ref))}
                onUpload={upload}
                uploading={uploading}
                manage={listingSlug && (
                  <ManageGalleriesLink recordPath="listings" recordId={listingSlug}
                                       recordName={listingName ?? 'Property'} onClose={reload} />
                )}
              />
            </>
          )}

          {dirty && (
            <Alert
              style={{ marginTop: 16 }}
              type="info"
              showIcon
              message="Unsaved — the brochure keeps its current photos until you save and re-render."
            />
          )}
        </Body>
        <PickerFooter>
          {onClose && <Button onClick={onClose}>Close</Button>}
          <Tooltip title={dirty ? '' : 'Nothing changed yet.'}>
            <Button type="primary" onClick={save} loading={saving} disabled={!dirty}>
              {onClose ? 'Save' : 'Save photos'}
            </Button>
          </Tooltip>
        </PickerFooter>
    </>
  );
};

const GalleryPicksModal = ({ children, listingName, ...editor }: GalleryPicksModalProps) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <span onClick={() => setOpen(true)}>{children}</span>
      <Modal
        visible={open}
        onCancel={() => setOpen(false)}
        title={`Gallery photos${listingName ? ` — ${listingName}` : ''}`}
        width={960}
        destroyOnClose
        footer={null}
      >
        {open && (
          <GalleryPicksEditor {...editor} listingName={listingName}
                              onClose={() => setOpen(false)} />
        )}
      </Modal>
    </>
  );
};

export default GalleryPicksModal;
